import { ISourceDataConfigurationItem } from "@/utilities/forms/ConfigurationFormProcessor";
import { alpha, Button, Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Slide, styled } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useFormActions } from "@ngt/forms-core";
import { set } from "lodash-es";
import { FC, forwardRef, Fragment, ReactElement, Ref, useEffect, useMemo, useState } from "react";
import { IConfigurationFormEditUpdateItem, ISelectionOption } from ".";
import { UpdateMapping } from "./UpdateMapping";

export const InfoBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(0, 2, 0, 2),
    margin: theme.spacing(2, 2, 2, 2)
}));

export const SelectionBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(0, 0, 0, 0),
    margin: theme.spacing(2, 2, 2, 2)
}));

export interface IConfigurationFormEditUpdateProps {
    target: IConfigurationFormEditUpdateItem;
    source: IConfigurationFormEditUpdateItem;
}

export const UpdateDialogTransition = forwardRef(function Transition(
    props: TransitionProps & {
      children: ReactElement;
    },
    ref: Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  
export const SelectionContainer = styled(List)(({theme}) => ({
}));

export const SelectionItem = styled(ListItem)(({theme}) => ({
    borderLeft: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`,
    borderTop: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`,
    borderBottom: theme.palette.mode === 'dark' ? 
        `solid 1px ${alpha(theme.palette.common.white, 0.12)}` :
        `solid 1px ${alpha(theme.palette.common.black, 0.12)}`,

    background: theme.palette.background.paper,
    backgroundImage: theme.palette.mode === 'dark' ? 
        `linear-gradient(${alpha(theme.palette.common.white, 0.05)}, ${alpha(theme.palette.common.white, 0.05)})` :
        undefined,
    marginTop: -1,
}));

type SelectionState = any;

export const Selections: FC<IConfigurationFormEditUpdateProps & { buttonText: string; onUpdate: () => void; }> = ({
    source,
    target,
    buttonText,
    onUpdate
}) => {
    const actions = useFormActions();
    const options = useMemo(() => {
        return UpdateMapping[source.type].createOptions(source, target)
    }, [source, target])

    const [state, setState] = useState<SelectionState>()

    useEffect(() => {
        const newState = {};

        const updateState = (option: ISelectionOption, parentName?: string) => {
            const name = parentName ?
                `${parentName}.${option.name}` :
                option.name;

            if (option.options && option.options.length > 0) {
                for (let childOption of option.options) {
                    updateState(childOption, name)
                }
            }
            else {
                set(newState, name, true);
            }
        }

        for (let o of options) {
            updateState(o)
        }

        setState(newState);

    }, [options, setState]);

    if (options.length === 0) {
        return null;
    }

    const setOption = (name: string, update: (currentValue: any) => any) => {
        setState((c: any) => {
            return {
                ...c,
                [name]: update(c[name])
            }   
        })
    }

    if (!state) {
        return null;
    }

    return (
        <>
            <SelectionBox>
                <Selection
                    options={options}
                    state={state}
                    setState={setOption}
                />
            </SelectionBox>
            <Button 
                onClick={() => {
                    const sourceValue = actions.getFieldValue<ISourceDataConfigurationItem>(source.configurationPropertyPath!)!;
                    const targetValue = actions.getFieldValue<ISourceDataConfigurationItem>(target.configurationPropertyPath!)!;

                    const newValue = UpdateMapping[source.type].update(source, target, sourceValue, targetValue, options, state);

                    actions.setFieldValue<ISourceDataConfigurationItem>(target.configurationPropertyPath!, newValue);

                    onUpdate();
                }}
            >
                {buttonText}
            </Button>
        </>
    );
}

const getTotals = (options: ISelectionOption[], state: any) => {
    let total = 0;
    let checked = 0;

    for (let option of options) {
        if (option.options) {
            const childTotals = getTotals(option.options, state[option.name]);

            total += childTotals.total;
            checked += childTotals.checked;
        }
        else {
            total++;

            if (state[option.name]) {
                checked++;
            }
        }
    }

    return {
        total,
        checked
    };
}

const setChildOptions = (options: ISelectionOption[], state: any, value: boolean) => {
    const newState = {...state};

    for (let option of options) {
        if (option.options && option.options.length > 0) {
            newState[option.name] = setChildOptions(option.options, newState[option.name], value);
        }
        else {
            newState[option.name] = value;
        }
    }

    return newState;
}

interface ISelectionProps {
    parentName?: string;
    options: ISelectionOption[];
    level?: number; 
    state: any;
    setState: (name: string, update: (currentValue: any) => any) => void;
}

const Selection: FC<ISelectionProps> = ({ options, level = 0, state, parentName, setState }) => {

    const handleToggle = (option: ISelectionOption) => () => {
        if (option.options && option.options.length > 0) {
            setState(option.name, current => {
                const totals = getTotals(option.options!, current);

                return setChildOptions(option.options!, current, totals.total !== totals.checked);
            });  
        }
        else {
            setState(option.name, current => !current);  
        }
    };

    const getSetState = (option: ISelectionOption) => (name: string, update: (currentValue: any) => any) => {
        setState(option.name, current => {
            return {
                ...current,
                [name]: update(current[name])
            }   
        })
    }

    return (
        <SelectionContainer disablePadding sx={{ml: level > 0 ? 8 : 0}}>
            {
                options.map(x => {
                    let checked = true;
                    let indeterminate = false;

                    if (x.options) {
                        const totals = getTotals(x.options, state[x.name]);

                        checked = totals.total === totals.checked;
                        indeterminate = !checked && totals.checked > 0;
                    }
                    else {
                        checked = !!state[x.name];
                    }

                    const labelId = parentName ? `${parentName}.${x.name}` : x.name

                    return (
                        <Fragment
                            key={x.name}
                        >
                            <SelectionItem
                                disablePadding
                            >
                                <ListItemButton 
                                    role={undefined} 
                                    onClick={handleToggle(x)} 
                                    dense
                                >
                                    <ListItemIcon>
                                        <Checkbox
                                            edge="start"
                                            checked={checked}
                                            indeterminate={indeterminate}
                                            tabIndex={-1}
                                            disableRipple
                                            inputProps={{ 'aria-labelledby': labelId }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText 
                                        id={labelId} 
                                        primary={x.label} 
                                    />
                                </ListItemButton>
                            </SelectionItem>
                            {
                                x.options && (
                                    <Selection parentName={x.name} options={x.options} level={level + 1} state={state[x.name]} setState={getSetState(x)} />
                                )
                            }
                        </Fragment>
                    )
                })
            }
        </SelectionContainer>
    )
};